"use client";

import { useEffect, useRef, useState } from "react";

import console from "@/utils/console";

interface AudioBlobVisualizerProps {
  mediaElement?: HTMLVideoElement | HTMLAudioElement | null;
  size?: number;
  color?: string;
  glowColor?: string;
  sensitivity?: number;
  smoothing?: number;
  className?: string;
  initialRadius?: number; // In pixels, like CSS border-radius
}

// Keep track of connected media elements globally to prevent duplicate connections
const connectedMediaElements = new WeakMap<HTMLMediaElement, AudioContext>();

export default function AudioBlobVisualizer({
  mediaElement,
  size = 100,
  color = "#4f46e5",
  glowColor = "#818cf8",
  sensitivity = 2.5,
  smoothing = 0.1,
  className,
  initialRadius = 50, // Default to 50px (half of default size, making a circle)
}: AudioBlobVisualizerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const timeRef = useRef(0);
  const prevPointsRef = useRef<Array<{ x: number; y: number }>>([]);

  // Audio analysis references
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const audioSetupCompleteRef = useRef(false);

  // Set up audio analysis when media element changes
  useEffect(() => {
    if (!mediaElement || audioSetupCompleteRef.current) return;

    const setupAudio = async () => {
      try {
        // Create audio context
        const AudioContext =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContext) {
          console.error("Web Audio API is not supported in this browser");
          return;
        }

        // Check if this media element is already connected to an audio context
        if (connectedMediaElements.has(mediaElement)) {
          // Reuse the existing audio context
          audioContextRef.current = connectedMediaElements.get(
            mediaElement,
          ) as AudioContext;

          // Create a new analyzer node in the existing context
          analyserRef.current = audioContextRef.current.createAnalyser();
          analyserRef.current.fftSize = 2048;
          analyserRef.current.smoothingTimeConstant = Math.min(
            0.95,
            Math.max(0.5, 1 - sensitivity * 0.3),
          );

          // Create data array for frequency analysis
          const bufferLength = analyserRef.current.frequencyBinCount;
          dataArrayRef.current = new Uint8Array(bufferLength);

          audioSetupCompleteRef.current = true;

          // Get the existing source node and connect it to our analyzer
          // We need to use the destination node as a way to tap into the existing audio graph
          const destination = audioContextRef.current.destination;
          analyserRef.current.connect(destination);
          console.log("Reusing existing audio context for media element");

          // Resume audio context if suspended
          if (audioContextRef.current.state === "suspended") {
            await audioContextRef.current.resume();
          }

          return;
        }

        // Create a new audio context if this is the first time connecting this media element
        audioContextRef.current = new AudioContext();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 2048;
        analyserRef.current.smoothingTimeConstant = Math.min(
          0.95,
          Math.max(0.5, 1 - sensitivity * 0.3),
        );

        // Create data array for frequency analysis
        const bufferLength = analyserRef.current.frequencyBinCount;
        dataArrayRef.current = new Uint8Array(bufferLength);

        // Connect media element to analyzer
        sourceNodeRef.current =
          audioContextRef.current.createMediaElementSource(mediaElement);
        sourceNodeRef.current.connect(analyserRef.current);
        analyserRef.current.connect(audioContextRef.current.destination);

        // Store the connection in our global WeakMap
        connectedMediaElements.set(mediaElement, audioContextRef.current);

        audioSetupCompleteRef.current = true;
        console.log("Audio analysis setup complete");

        // Resume audio context if suspended
        if (audioContextRef.current.state === "suspended") {
          await audioContextRef.current.resume();
        }
      } catch (error) {
        console.error("Error setting up audio analysis:", error);
      }
    };

    setupAudio();

    // Clean up audio nodes when component unmounts or media element changes
    return () => {
      if (analyserRef.current) {
        analyserRef.current.disconnect();
      }

      // We don't disconnect the source node or close the audio context
      // because other components might be using the same media element

      // Reset setup flag so we can set up again with a new media element
      audioSetupCompleteRef.current = false;
    };
  }, [mediaElement, sensitivity]);

  // Add a separate useEffect to update analyzer parameters when sensitivity changes
  useEffect(() => {
    if (analyserRef.current) {
      analyserRef.current.smoothingTimeConstant = Math.min(
        0.95,
        Math.max(0.5, 1 - sensitivity * 0.3),
      );
    }
  }, [sensitivity]);

  // Listen to media events to update internal state
  useEffect(() => {
    if (!mediaElement) return;

    const handlePlay = () => {
      console.log("Media play event detected");
      setIsPlaying(true);

      // Resume audio context if it's suspended
      if (
        audioContextRef.current &&
        audioContextRef.current.state === "suspended"
      ) {
        audioContextRef.current.resume().catch((e) => {
          console.error("Failed to resume audio context:", e);
        });
      }
    };

    const handlePause = () => {
      console.log("Media pause event detected");
      setIsPlaying(false);
    };

    // Add event listeners
    mediaElement.addEventListener("play", handlePlay);
    mediaElement.addEventListener("playing", handlePlay);
    mediaElement.addEventListener("pause", handlePause);
    mediaElement.addEventListener("ended", handlePause);

    // Initial state
    setIsPlaying(!mediaElement.paused);

    // Cleanup
    return () => {
      mediaElement.removeEventListener("play", handlePlay);
      mediaElement.removeEventListener("playing", handlePlay);
      mediaElement.removeEventListener("pause", handlePause);
      mediaElement.removeEventListener("ended", handlePause);
    };
  }, [mediaElement]);

  // Get frequency data from analyzer
  const getFrequencyData = () => {
    if (!analyserRef.current || !dataArrayRef.current) {
      return {
        bass: 0.2,
        mid: 0.2,
        high: 0.2,
        average: 0.2,
      };
    }

    try {
      // Get frequency data
      analyserRef.current.getByteFrequencyData(dataArrayRef.current);

      // Calculate averages for different frequency bands
      const bass = getAverageFrequency(20, 250);
      const mid = getAverageFrequency(250, 2000);
      const high = getAverageFrequency(2000, 20000);

      // Calculate overall average
      let sum = 0;
      for (let i = 0; i < dataArrayRef.current.length; i++) {
        sum += dataArrayRef.current[i];
      }
      const average = sum / dataArrayRef.current.length / 255;

      return {
        bass,
        mid,
        high,
        average,
      };
    } catch (error) {
      console.error("Error analyzing audio:", error);
      return {
        bass: 0.2,
        mid: 0.2,
        high: 0.2,
        average: 0.2,
      };
    }
  };

  // Helper to get average frequency in a range
  const getAverageFrequency = (lowFreq: number, highFreq: number) => {
    if (!analyserRef.current || !dataArrayRef.current) return 0.2;

    const sampleRate = audioContextRef.current?.sampleRate || 44100;
    const binCount = analyserRef.current.frequencyBinCount;
    const binSize = sampleRate / analyserRef.current.fftSize;

    const lowBin = Math.floor(lowFreq / binSize);
    const highBin = Math.min(Math.floor(highFreq / binSize), binCount - 1);

    let sum = 0;
    let count = 0;

    for (let i = lowBin; i <= highBin; i++) {
      sum += dataArrayRef.current[i];
      count++;
    }

    return count > 0 ? sum / count / 255 : 0.2;
  };

  // Animation function
  const animate = () => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Get the actual canvas size
    const canvasSize = canvas.width / (window.devicePixelRatio || 1);

    // Clear canvas
    ctx.clearRect(0, 0, canvasSize, canvasSize);

    // Get frequency data
    const { bass, mid, high, average } = getFrequencyData();

    // Draw visualization
    drawBlobVisualization(ctx, bass, mid, high, average, canvasSize);

    // Continue animation
    animationRef.current = requestAnimationFrame(animate);
  };

  // Draw blob visualization
  const drawBlobVisualization = (
    ctx: CanvasRenderingContext2D,
    bass: number,
    mid: number,
    high: number,
    average: number,
    canvasSize: number,
  ) => {
    // Increment time for animation
    timeRef.current += 0.01;

    // Center of canvas
    const centerX = canvasSize / 2;
    const centerY = canvasSize / 2;

    // Base radius - keep the visualization size the same
    const baseRadius = size * 0.4;
    const numPoints = 24;

    // Generate base points for a circle (always start with a circle for the audio visualization)
    const basePoints = [];
    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * Math.PI * 2;
      basePoints.push({
        x: centerX + Math.cos(angle) * baseRadius,
        y: centerY + Math.sin(angle) * baseRadius,
      });
    }

    // Now apply audio-reactive deformations to the base points
    const points = [];

    for (let i = 0; i < basePoints.length; i++) {
      const basePoint = basePoints[i];

      // Calculate angle from center to this point
      const dx = basePoint.x - centerX;
      const dy = basePoint.y - centerY;
      const angle = Math.atan2(dy, dx);
      const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);

      // Use different frequency bands for different parts of the blob
      let radiusMultiplier;

      if (i % 3 === 0) {
        // Bass affects these points more
        radiusMultiplier = bass * 0.8 + mid * 0.1 + high * 0.1;
      } else if (i % 2 === 0) {
        // Mids affect these points more
        radiusMultiplier = bass * 0.1 + mid * 0.8 + high * 0.1;
      } else {
        // Highs affect these points more
        radiusMultiplier = bass * 0.1 + mid * 0.1 + high * 0.8;
      }

      // Add some noise based on position and time, but with reduced amplitude
      const noise = Math.sin(angle * 5 + timeRef.current) * 0.05;

      // Calculate deformation amount
      const deformation = (radiusMultiplier * 0.3 + noise) * sensitivity;

      // Apply deformation to the distance from center
      const newDistance = distanceFromCenter * (1.0 + deformation);

      // Calculate new point position
      const x = centerX + Math.cos(angle) * newDistance;
      const y = centerY + Math.sin(angle) * newDistance;

      points.push({ x, y });
    }

    // Apply temporal smoothing if we have previous points
    if (prevPointsRef.current.length === points.length) {
      for (let i = 0; i < points.length; i++) {
        points[i].x =
          points[i].x * (1 - smoothing) +
          prevPointsRef.current[i].x * smoothing;
        points[i].y =
          points[i].y * (1 - smoothing) +
          prevPointsRef.current[i].y * smoothing;
      }
    }

    // Store current points for next frame
    prevPointsRef.current = [...points];

    // Draw the blob
    ctx.beginPath();

    // Start at the first point
    ctx.moveTo(points[0].x, points[0].y);

    // Draw smooth curves through all points
    for (let i = 0; i < points.length; i++) {
      const p0 = points[(i - 1 + points.length) % points.length];
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      const p3 = points[(i + 2) % points.length];

      // Calculate control points for smooth curve
      const cp1x = p1.x + (p2.x - p0.x) * 0.2;
      const cp1y = p1.y + (p2.y - p0.y) * 0.2;
      const cp2x = p2.x - (p3.x - p1.x) * 0.2;
      const cp2y = p2.y - (p3.y - p1.y) * 0.2;

      // Draw curve to next point
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
    }

    ctx.closePath();

    // Create gradient fill based on audio intensity
    const gradient = ctx.createRadialGradient(
      centerX,
      centerY,
      baseRadius * 0.1,
      centerX,
      centerY,
      baseRadius * (1.1 + average * 0.3),
    );

    // Convert hex colors to RGB
    const getRgbFromHex = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: Number.parseInt(result[1], 16),
            g: Number.parseInt(result[2], 16),
            b: Number.parseInt(result[3], 16),
          }
        : { r: 0, g: 0, b: 0 };
    };

    // Use the user-selected colors for the gradient
    const primaryRgb = getRgbFromHex(color);
    const glowRgb = getRgbFromHex(glowColor);

    gradient.addColorStop(
      0,
      `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.9)`,
    );
    gradient.addColorStop(
      1,
      `rgba(${glowRgb.r}, ${glowRgb.g}, ${glowRgb.b}, 0.2)`,
    );

    ctx.fillStyle = gradient;
    ctx.fill();

    // Add glow effect
    ctx.shadowColor = color;
    ctx.shadowBlur = 15 * average * sensitivity;
    ctx.strokeStyle = glowColor;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Reset shadow
    ctx.shadowBlur = 0;
  };

  // Helper function to draw a rounded rectangle
  const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ) => {
    // Ensure radius doesn't exceed half the width or height
    const maxRadius = Math.min(width / 2, height / 2);
    const cornerRadius = Math.min(radius, maxRadius);

    ctx.beginPath();
    ctx.moveTo(x + cornerRadius, y);
    ctx.lineTo(x + width - cornerRadius, y);
    ctx.arcTo(x + width, y, x + width, y + cornerRadius, cornerRadius);
    ctx.lineTo(x + width, y + height - cornerRadius);
    ctx.arcTo(
      x + width,
      y + height,
      x + width - cornerRadius,
      y + height,
      cornerRadius,
    );
    ctx.lineTo(x + cornerRadius, y + height);
    ctx.arcTo(x, y + height, x, y + height - cornerRadius, cornerRadius);
    ctx.lineTo(x, y + cornerRadius);
    ctx.arcTo(x, y, x + cornerRadius, y, cornerRadius);
    ctx.closePath();
  };

  // Draw static visualization when paused
  const drawStaticVisualization = (
    ctx: CanvasRenderingContext2D,
    canvasSize: number,
  ) => {
    // Clear background for transparency
    ctx.clearRect(0, 0, canvasSize, canvasSize);

    // Center of canvas
    const centerX = canvasSize / 2;
    const centerY = canvasSize / 2;

    // Base radius - keep the visualization size the same
    const baseRadius = size * 0.4;

    // Draw the shape based on initialRadius
    ctx.beginPath();

    if (initialRadius >= baseRadius) {
      // Draw a circle
      ctx.arc(centerX, centerY, baseRadius, 0, Math.PI * 2);
    } else if (initialRadius <= 0) {
      // Draw a perfect square
      const halfSize = baseRadius;
      ctx.rect(
        centerX - halfSize,
        centerY - halfSize,
        halfSize * 2,
        halfSize * 2,
      );
    } else {
      // Draw a rounded rectangle using native roundRect if available
      const halfSize = baseRadius;
      if (ctx.roundRect) {
        // Use native roundRect if available (modern browsers)
        ctx.roundRect(
          centerX - halfSize,
          centerY - halfSize,
          halfSize * 2,
          halfSize * 2,
          initialRadius,
        );
      } else {
        // Fallback for browsers without roundRect
        drawRoundedRect(
          ctx,
          centerX - halfSize,
          centerY - halfSize,
          halfSize * 2,
          halfSize * 2,
          initialRadius,
        );
      }
    }

    ctx.closePath();

    // Create gradient fill
    const gradient = ctx.createRadialGradient(
      centerX,
      centerY,
      baseRadius * 0.1,
      centerX,
      centerY,
      baseRadius * 1.1,
    );

    // Convert hex colors to RGB
    const getRgbFromHex = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: Number.parseInt(result[1], 16),
            g: Number.parseInt(result[2], 16),
            b: Number.parseInt(result[3], 16),
          }
        : { r: 0, g: 0, b: 0 };
    };

    // Use the user-selected colors for the gradient
    const primaryRgb = getRgbFromHex(color);
    const glowRgb = getRgbFromHex(glowColor);

    gradient.addColorStop(
      0,
      `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.7)`,
    );
    gradient.addColorStop(
      1,
      `rgba(${glowRgb.r}, ${glowRgb.g}, ${glowRgb.b}, 0.2)`,
    );

    ctx.fillStyle = gradient;
    ctx.fill();

    // Add subtle glow
    ctx.shadowColor = color;
    ctx.shadowBlur = 8;
    ctx.strokeStyle = glowColor;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Reset shadow
    ctx.shadowBlur = 0;
  };

  // Handle animation based on play state
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Make canvas bigger than the visualization to prevent overflow
    // Add 40% padding around the visualization
    const canvasSize = size * 2.0;

    // Set canvas size with higher resolution for retina displays
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasSize * dpr;
    canvas.height = canvasSize * dpr;

    // Scale the canvas back down with CSS
    canvas.style.width = `${canvasSize}px`;
    canvas.style.height = `${canvasSize}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Scale all drawing operations by dpr for retina displays
    ctx.scale(dpr, dpr);

    // Clear canvas initially
    ctx.clearRect(0, 0, canvasSize, canvasSize);

    if (isPlaying) {
      console.log("Starting visualization animation");
      animate();
    } else {
      console.log("Stopping visualization animation");
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Draw static visualization when paused
      drawStaticVisualization(ctx, canvasSize);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, size, initialRadius, color, glowColor]);

  // Always render the canvas, even if no media element is provided
  const canvasSize = size * 2.0;

  // If no media element, draw static visualization on mount
  useEffect(() => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      if (ctx) {
        // Set canvas size with higher resolution for retina displays
        const dpr = window.devicePixelRatio || 1;
        canvasRef.current.width = canvasSize * dpr;
        canvasRef.current.height = canvasSize * dpr;

        // Scale all drawing operations by dpr for retina displays
        ctx.scale(dpr, dpr);

        // Draw static visualization
        drawStaticVisualization(ctx, canvasSize);
      }
    }
  }, [canvasSize, size, color, glowColor, initialRadius]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        width: `${canvasSize}px`,
        height: `${canvasSize}px`,
      }}
    />
  );
}
